import * as React from "react";

import twMerge from "@replo/design-system/utils/twMerge";
import useCreateComponentForm from "@v1/hooks/useCreateComponentForm";
import {
  selectLeftBarActiveTab,
  selectLeftBarWidth,
  setLeftBarActiveTab,
} from "@v1/reducers/ui-reducer";
import { useEditorDispatch, useEditorSelector } from "@v1/store/store";
import { useCurrentProjectContext } from "@v2/contexts/CurrentProjectContext";

import LeftBarHeader from "./LeftBarHeader";
import LeftBarPanel from "./LeftBarPanel";

interface LeftBarProps {
  showElementSpecificContent: boolean;
  fullPageOverlay?: React.ReactNode;
}

const LeftBar: React.FC<LeftBarProps> = React.memo(function LeftBar({
  showElementSpecificContent,
  fullPageOverlay,
}: LeftBarProps) {
  const { isLoading } = useCurrentProjectContext();
  const dispatch = useEditorDispatch();
  const isFullPageOverlay = Boolean(fullPageOverlay);
  const leftBarActiveTab = useEditorSelector(selectLeftBarActiveTab);
  const leftBarWidth = useEditorSelector(selectLeftBarWidth);
  const isLeftBarOpen = leftBarActiveTab !== null;

  useCreateComponentForm(() => dispatch(setLeftBarActiveTab("components")));

  if (isLoading) {
    return null;
  }
  if (isFullPageOverlay) {
    return (
      <div
        className="bg-neutral-soft absolute h-full w-full"
        style={{ top: 0, left: 0 }}
      >
        <LeftBarHeader
          showElementSpecificContent={showElementSpecificContent}
          isFullPageOverlay={isFullPageOverlay}
        />
        <LeftBarPanel showElementSpecificContent={showElementSpecificContent} />
        <div
          className={twMerge(
            "transition-[margin-left]",
            isLeftBarOpen ? "" : "ml-0",
          )}
          style={{
            marginLeft: isLeftBarOpen ? `${leftBarWidth}px` : "0",
          }}
        >
          {fullPageOverlay}
        </div>
      </div>
    );
  }

  return (
    <>
      <LeftBarHeader
        showElementSpecificContent={showElementSpecificContent}
        isFullPageOverlay={isFullPageOverlay}
      />
      <LeftBarPanel showElementSpecificContent={showElementSpecificContent} />
    </>
  );
});

export default LeftBar;
